import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { getFavorites, useLibraryVersion } from "@/lib/library";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影" },
  { to: "/category/tv", label: "电视剧" },
  { to: "/category/variety", label: "综艺" },
  { to: "/news", label: "资讯" },
  { to: "/about", label: "关于我们" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useLibraryVersion();
  const favCount = getFavorites().length;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : "/category");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="糖心传媒首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
              糖心传媒
            </span>
            <span className="hidden text-[10px] tracking-[0.2em] text-foreground-500 sm:block">
              TANGXIN MEDIA
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Search */}
        <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 md:block lg:max-w-sm">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              type="search"
              name="keyword"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品、资讯…"
              className="w-full rounded-full border border-background-300 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </form>

        {/* Quick actions */}
        <div className="ml-auto flex items-center gap-1 md:ml-0">
          <Link
            to="/favorites"
            className="relative flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
            aria-label="我的收藏"
          >
            <i className="ri-heart-3-line text-xl" />
            {favCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-background-50">
                {favCount > 99 ? "99+" : favCount}
              </span>
            )}
          </Link>
          <Link
            to="/history"
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
            aria-label="浏览记录"
          >
            <i className="ri-history-line text-xl" />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <form onSubmit={submitSearch} className="mb-3 md:hidden">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视作品、资讯…"
                className="w-full rounded-full border border-background-300 bg-background-100 py-2 pl-9 pr-4 text-sm outline-none focus:border-primary-400"
              />
            </div>
          </form>
          <nav className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "bg-background-100 text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/favorites"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg bg-secondary-100 px-3 py-2.5 text-sm font-medium text-secondary-900"
            >
              我的收藏
            </NavLink>
            <NavLink
              to="/history"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg bg-secondary-100 px-3 py-2.5 text-sm font-medium text-secondary-900"
            >
              浏览记录
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}